import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type * as GeoJSON from "geojson";
import { StoryCamera, StoryTransition } from "../../types/storyMap";
import {
  boundaryToFeature,
  MAPBOX_ACCESS_TOKEN,
  StoryBoundaryInput,
  StoryCanvasLayer,
} from "./storyLayers";
import { reportLayoutSizeToMapbox } from "../../utils/uiZoom";

export type { StoryBoundaryInput };

// Same hardcoded token/style as the main Map.tsx — this component is a
// deliberately lightweight standalone map for story authoring/reading.
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const MAP_STYLE = "mapbox://styles/mapbox/satellite-streets-v12";
const DEM_SOURCE_ID = "mapbox-dem";

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

export interface StoryMapCanvasHandle {
  /** Current camera, for the builder's "Use current view" button. */
  getCamera: () => StoryCamera | null;
  /** Programmatic instant camera move (compare-map sync). */
  jumpTo: (camera: StoryCamera) => void;
  /** Underlying mapbox map (print capture: idle waits + toDataURL). */
  getMap: () => mapboxgl.Map | null;
}

interface StoryMapCanvasProps {
  /** Declarative layer stack, bottom→top (see storyLayers.ts builders). */
  layers?: StoryCanvasLayer[];
  /** Fit the initial view to this feature when no camera is set. */
  fitTo?: StoryBoundaryInput | null;
  /**
   * Camera applied IN the mapboxgl.Map constructor (no post-load flyTo), so
   * the map first paints at the right view. Falls back to `camera`, then
   * `initialBounds`. Read once at construction.
   */
  initialCamera?: StoryCamera | null;
  /**
   * Constructor-time fit fallback when neither `camera` nor `initialCamera`
   * is set: the map is constructed with `bounds` of this feature. Read once
   * at construction.
   */
  initialBounds?: StoryBoundaryInput | null;
  camera?: StoryCamera | null;
  transition?: StoryTransition;
  interactive?: boolean;
  /**
   * Keep the WebGL drawing buffer readable so getCanvas().toDataURL() works
   * (print capture only — costs memory/perf, default false). Constructor-time.
   */
  preserveDrawingBuffer?: boolean;
  style?: React.CSSProperties;
  onMoveEnd?: (camera: StoryCamera) => void;
  /**
   * Fired when a feature of a layer declared `interactive: true` is clicked
   * (top-most feature wins). `layerId` is the declared StoryCanvasLayer id.
   */
  onLayerClick?: (layerId: string, feature: GeoJSON.Feature) => void;
  /**
   * Fired on any map click that didn't hit an interactive layer feature
   * (builder: inspection-point picking).
   */
  onMapClick?: (lngLat: { lng: number; lat: number }) => void;
  /**
   * The mapbox map once its style has loaded (null again on teardown) — for
   * DOM overlays that need the instance as React state (wind particles).
   */
  onMapReady?: (map: mapboxgl.Map | null) => void;
}

function bboxOfFeature(
  feature: GeoJSON.Feature
): [number, number, number, number] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const walk = (coords: unknown) => {
    if (!Array.isArray(coords)) return;
    if (
      coords.length >= 2 &&
      typeof coords[0] === "number" &&
      typeof coords[1] === "number"
    ) {
      west = Math.min(west, coords[0]);
      east = Math.max(east, coords[0]);
      south = Math.min(south, coords[1]);
      north = Math.max(north, coords[1]);
      return;
    }
    coords.forEach(walk);
  };
  const geometry = feature.geometry as { coordinates?: unknown } | null;
  walk(geometry?.coordinates);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
  return [west, south, east, north];
}

function readCamera(map: mapboxgl.Map): StoryCamera {
  const center = map.getCenter();
  const round = (v: number, dp: number) => Number(v.toFixed(dp));
  return {
    center: [round(center.lng, 6), round(center.lat, 6)],
    zoom: round(map.getZoom(), 2),
    pitch: round(map.getPitch(), 1),
    bearing: round(map.getBearing(), 1),
  };
}

function setHandlers(map: mapboxgl.Map, enabled: boolean) {
  const handlers = [
    map.scrollZoom,
    map.boxZoom,
    map.dragRotate,
    map.dragPan,
    map.keyboard,
    map.doubleClickZoom,
    map.touchZoomRotate,
  ];
  handlers.forEach((h) => (enabled ? h.enable() : h.disable()));
}

/** Mapbox layer ids a declared story layer expands to (bottom→top). */
function subLayerIds(spec: StoryCanvasLayer): string[] {
  switch (spec.kind) {
    case "geojson-line":
      return [`${spec.id}-line`];
    case "geojson-fill":
      return spec.linePaint
        ? [`${spec.id}-fill`, `${spec.id}-line`]
        : [`${spec.id}-fill`];
    case "geojson-circle":
      return [`${spec.id}-circle`];
    case "geojson-symbol":
      return [`${spec.id}-symbol`];
    case "vector":
      return spec.linePaint
        ? [`${spec.id}-fill`, `${spec.id}-line`]
        : [`${spec.id}-fill`];
    case "image":
      return [`${spec.id}-raster`];
    case "canvas-anim":
      return [`${spec.id}-raster`];
  }
}

/**
 * Structural identity of a layer: when it changes the source+layers are torn
 * down and re-added. GeoJSON data and visibility update in place; the canvas
 * element is compared by reference separately.
 */
function layerSignature(spec: StoryCanvasLayer): string {
  switch (spec.kind) {
    case "geojson-line":
      return JSON.stringify([
        "gl",
        spec.paint,
        spec.layout ?? null,
        spec.filter ?? null,
        spec.minzoom ?? null,
      ]);
    case "geojson-symbol":
      return JSON.stringify([
        "gs",
        spec.paint,
        spec.layout,
        spec.image?.id ?? null,
        spec.filter ?? null,
        spec.minzoom ?? null,
      ]);
    case "geojson-fill":
      return JSON.stringify([
        "gf",
        spec.fillPaint,
        spec.linePaint ?? null,
        spec.lineLayout ?? null,
        spec.pattern?.id ?? null,
        spec.filter ?? null,
      ]);
    case "geojson-circle":
      return JSON.stringify(["gc", spec.paint, spec.filter ?? null]);
    case "vector":
      return JSON.stringify([
        "v",
        spec.url,
        spec.sourceLayer,
        spec.paint,
        spec.linePaint ?? null,
        spec.pattern?.id ?? null,
        spec.minzoom,
        spec.maxzoom,
        spec.tileSize,
        spec.promoteId,
      ]);
    case "image":
      return JSON.stringify([
        "i",
        spec.url,
        spec.coordinates,
        spec.opacity ?? 0.85,
      ]);
    case "canvas-anim":
      return JSON.stringify([
        "c",
        spec.coordinates,
        spec.opacity ?? 0.85,
        spec.animate ?? true,
      ]);
  }
}

function removeStoryLayer(map: mapboxgl.Map, spec: StoryCanvasLayer) {
  subLayerIds(spec).forEach((id) => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  if (map.getSource(spec.id)) map.removeSource(spec.id);
}

/** Register a fill-pattern sprite once per map (ids encode pattern+colour). */
function ensurePattern(
  map: mapboxgl.Map,
  pattern: { id: string; data: ImageData } | undefined
) {
  if (pattern && !map.hasImage(pattern.id)) {
    map.addImage(pattern.id, pattern.data, { pixelRatio: 1 });
  }
}

function addStoryLayer(map: mapboxgl.Map, spec: StoryCanvasLayer) {
  const visibility = spec.visible ? "visible" : "none";
  switch (spec.kind) {
    case "geojson-line": {
      map.addSource(spec.id, { type: "geojson", data: spec.data ?? EMPTY_FC });
      map.addLayer({
        id: `${spec.id}-line`,
        type: "line",
        source: spec.id,
        paint: spec.paint,
        ...(spec.filter ? { filter: spec.filter } : {}),
        ...(spec.minzoom != null ? { minzoom: spec.minzoom } : {}),
        layout: { ...spec.layout, visibility },
      });
      break;
    }
    case "geojson-symbol": {
      // Register the icon once per map; SDF images stay registered on layer
      // removal so re-adds (and other story layers) can share them.
      if (spec.image && !map.hasImage(spec.image.id)) {
        map.addImage(spec.image.id, spec.image.data, { sdf: spec.image.sdf });
      }
      map.addSource(spec.id, { type: "geojson", data: spec.data ?? EMPTY_FC });
      map.addLayer({
        id: `${spec.id}-symbol`,
        type: "symbol",
        source: spec.id,
        paint: spec.paint,
        ...(spec.filter ? { filter: spec.filter } : {}),
        ...(spec.minzoom != null ? { minzoom: spec.minzoom } : {}),
        layout: { ...spec.layout, visibility },
      });
      break;
    }
    case "geojson-fill": {
      ensurePattern(map, spec.pattern);
      map.addSource(spec.id, { type: "geojson", data: spec.data ?? EMPTY_FC });
      map.addLayer({
        id: `${spec.id}-fill`,
        type: "fill",
        source: spec.id,
        paint: spec.fillPaint,
        ...(spec.filter ? { filter: spec.filter } : {}),
        layout: { visibility },
      });
      if (spec.linePaint) {
        map.addLayer({
          id: `${spec.id}-line`,
          type: "line",
          source: spec.id,
          paint: spec.linePaint,
          ...(spec.filter ? { filter: spec.filter } : {}),
          layout: { ...spec.lineLayout, visibility },
        });
      }
      break;
    }
    case "geojson-circle": {
      map.addSource(spec.id, { type: "geojson", data: spec.data ?? EMPTY_FC });
      map.addLayer({
        id: `${spec.id}-circle`,
        type: "circle",
        source: spec.id,
        paint: spec.paint,
        ...(spec.filter ? { filter: spec.filter } : {}),
        layout: { visibility },
      });
      break;
    }
    case "vector": {
      ensurePattern(map, spec.pattern);
      map.addSource(spec.id, {
        type: "vector",
        tiles: [spec.url],
        minzoom: spec.minzoom,
        maxzoom: spec.maxzoom,
        tileSize: spec.tileSize,
        promoteId: spec.promoteId,
      } as mapboxgl.VectorSourceSpecification);
      map.addLayer({
        id: `${spec.id}-fill`,
        type: "fill",
        source: spec.id,
        "source-layer": spec.sourceLayer,
        paint: spec.paint,
        layout: { visibility },
      });
      if (spec.linePaint) {
        map.addLayer({
          id: `${spec.id}-line`,
          type: "line",
          source: spec.id,
          "source-layer": spec.sourceLayer,
          paint: spec.linePaint,
          layout: { visibility },
        });
      }
      break;
    }
    case "image": {
      map.addSource(spec.id, {
        type: "image",
        url: spec.url,
        coordinates: spec.coordinates,
      });
      map.addLayer({
        id: `${spec.id}-raster`,
        type: "raster",
        source: spec.id,
        paint: {
          "raster-opacity": spec.opacity ?? 0.85,
          "raster-fade-duration": 0,
          "raster-resampling": "nearest",
        },
        layout: { visibility },
      });
      break;
    }
    case "canvas-anim": {
      // Canvas sources exist at runtime but aren't in the SourceSpecification
      // union (same workaround as Map.tsx).
      map.addSource(spec.id, {
        type: "canvas",
        canvas: spec.canvas,
        animate: spec.animate ?? true,
        coordinates: spec.coordinates,
      } as unknown as mapboxgl.SourceSpecification);
      map.addLayer({
        id: `${spec.id}-raster`,
        type: "raster",
        source: spec.id,
        paint: {
          "raster-opacity": spec.opacity ?? 0.85,
          "raster-fade-duration": 0,
          "raster-resampling": "nearest",
        },
        layout: { visibility },
      });
      break;
    }
  }
}

/**
 * Self-contained mapbox-gl map for Story Maps (builder + viewer). Renders a
 * declarative layer list (geojson line/fill, vector tiles, canvas animation).
 * Not the project-page Map.tsx, which is coupled to project-page contexts.
 */
const StoryMapCanvas = forwardRef<StoryMapCanvasHandle, StoryMapCanvasProps>(
  (
    {
      layers = [],
      fitTo,
      initialCamera,
      initialBounds,
      camera,
      transition = "fly",
      interactive = true,
      preserveDrawingBuffer = false,
      style,
      onMoveEnd,
      onLayerClick,
      onMapClick,
      onMapReady,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [loaded, setLoaded] = useState(false);
    const appliedCameraRef = useRef<string | null>(null);
    // True when the constructor already established a real view (camera or
    // bounds), so the post-load fitTo fallback must not re-fit.
    const initialViewAppliedRef = useRef(false);
    // Per-layer-id bookkeeping of what is currently attached to the map.
    const attachedRef = useRef<
      Map<string, { signature: string; canvas: HTMLCanvasElement | null }>
    >(new Map());
    const attachedSpecsRef = useRef<Map<string, StoryCanvasLayer>>(new Map());
    // Mapbox sub-layer id -> declared layer id, for interactive layers only.
    const interactiveIdsRef = useRef<Map<string, string>>(new Map());
    const onMoveEndRef = useRef(onMoveEnd);
    onMoveEndRef.current = onMoveEnd;
    const onLayerClickRef = useRef(onLayerClick);
    onLayerClickRef.current = onLayerClick;
    const onMapClickRef = useRef(onMapClick);
    onMapClickRef.current = onMapClick;
    const onMapReadyRef = useRef(onMapReady);
    onMapReadyRef.current = onMapReady;

    useImperativeHandle(ref, () => ({
      getCamera: () => (mapRef.current ? readCamera(mapRef.current) : null),
      jumpTo: (target: StoryCamera) => {
        appliedCameraRef.current = JSON.stringify(target);
        mapRef.current?.jumpTo({
          center: target.center,
          zoom: target.zoom,
          pitch: target.pitch,
          bearing: target.bearing,
        });
      },
      getMap: () => mapRef.current,
    }));

    useEffect(() => {
      if (!containerRef.current || mapRef.current) return;
      // Initial view resolved at construction (no post-load flyTo): the scene
      // camera, else the caller's initialCamera, else fitBounds of
      // initialBounds via the constructor's `bounds` option.
      const startCamera = camera ?? initialCamera ?? null;
      const initialFitFeature = startCamera
        ? null
        : boundaryToFeature(initialBounds);
      const initialFitBbox = initialFitFeature
        ? bboxOfFeature(initialFitFeature)
        : null;
      let map: mapboxgl.Map;
      // The UI may be scaled with CSS zoom on small screens (index.css).
      reportLayoutSizeToMapbox(containerRef.current);
      try {
        map = new mapboxgl.Map({
          container: containerRef.current,
          style: MAP_STYLE,
          center: startCamera?.center ?? [149.13, -35.28],
          zoom: startCamera?.zoom ?? 4,
          pitch: startCamera?.pitch ?? 0,
          bearing: startCamera?.bearing ?? 0,
          ...(initialFitBbox
            ? { bounds: initialFitBbox, fitBoundsOptions: { padding: 60 } }
            : {}),
          preserveDrawingBuffer,
          attributionControl: false,
        });
      } catch (error) {
        // No WebGL (or a driver that refuses a context): leave the container
        // empty rather than unmounting the page. Callers that can show a
        // still instead check webglAvailable() (./webgl.ts) before
        // rendering this.
        console.warn("StoryMapCanvas: could not create a map", error);
        return;
      }
      if (startCamera) appliedCameraRef.current = JSON.stringify(startCamera);
      initialViewAppliedRef.current = !!startCamera || !!initialFitBbox;
      map.addControl(new mapboxgl.AttributionControl({ compact: true }));
      map.on("load", () => {
        // Same 3D terrain as the project map (Map.tsx) so pitched story
        // scenes show relief rather than a flat satellite image.
        if (!map.getSource(DEM_SOURCE_ID)) {
          map.addSource(DEM_SOURCE_ID, {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14,
          });
        }
        map.setTerrain({ source: DEM_SOURCE_ID, exaggeration: 1.5 });
        setLoaded(true);
        onMapReadyRef.current?.(map);
      });
      map.on("moveend", () => {
        onMoveEndRef.current?.(readCamera(map));
      });
      // Delegated click/cursor handling for layers declared interactive.
      const hitLayers = (point: mapboxgl.Point) => {
        const ids = Array.from(interactiveIdsRef.current.keys()).filter((id) =>
          map.getLayer(id)
        );
        if (!ids.length) return [];
        return map.queryRenderedFeatures(point, { layers: ids });
      };
      map.on("click", (e) => {
        const features = onLayerClickRef.current ? hitLayers(e.point) : [];
        const feature = features[0];
        if (feature) {
          const declaredId = interactiveIdsRef.current.get(
            feature.layer?.id ?? ""
          );
          if (declaredId && onLayerClickRef.current) {
            onLayerClickRef.current(declaredId, feature as GeoJSON.Feature);
            return;
          }
        }
        onMapClickRef.current?.({ lng: e.lngLat.lng, lat: e.lngLat.lat });
      });
      map.on("mousemove", (e) => {
        if (!onLayerClickRef.current) return;
        map.getCanvas().style.cursor = hitLayers(e.point).length
          ? "pointer"
          : "";
      });
      mapRef.current = map;
      const attached = attachedRef.current;
      const attachedSpecs = attachedSpecsRef.current;
      return () => {
        onMapReadyRef.current?.(null);
        map.remove();
        mapRef.current = null;
        attached.clear();
        attachedSpecs.clear();
        setLoaded(false);
      };
      // Initial camera only — subsequent camera changes fly/jump below.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reconcile the declared layer list against what is attached.
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !loaded) return;

      const wanted = new Set(layers.map((l) => l.id));
      attachedRef.current.forEach((_, id) => {
        if (!wanted.has(id)) {
          const spec = attachedSpecsRef.current.get(id);
          if (spec) removeStoryLayer(map, spec);
          attachedRef.current.delete(id);
          attachedSpecsRef.current.delete(id);
        }
      });

      layers.forEach((spec) => {
        const signature = layerSignature(spec);
        const canvas = spec.kind === "canvas-anim" ? spec.canvas : null;
        const current = attachedRef.current.get(spec.id);
        if (
          !current ||
          current.signature !== signature ||
          current.canvas !== canvas
        ) {
          const oldSpec = attachedSpecsRef.current.get(spec.id);
          if (oldSpec) removeStoryLayer(map, oldSpec);
          addStoryLayer(map, spec);
          attachedRef.current.set(spec.id, { signature, canvas });
          attachedSpecsRef.current.set(spec.id, spec);
        } else {
          // In-place updates: geojson data + visibility.
          if (
            spec.kind === "geojson-line" ||
            spec.kind === "geojson-fill" ||
            spec.kind === "geojson-circle" ||
            spec.kind === "geojson-symbol"
          ) {
            const source = map.getSource(spec.id) as
              | mapboxgl.GeoJSONSource
              | undefined;
            source?.setData(spec.data ?? EMPTY_FC);
          }
          const visibility = spec.visible ? "visible" : "none";
          subLayerIds(spec).forEach((id) => {
            if (map.getLayer(id)) {
              map.setLayoutProperty(id, "visibility", visibility);
            }
          });
          attachedSpecsRef.current.set(spec.id, spec);
        }
      });

      // Enforce declaration order (bottom→top): moving each to the top in
      // array order yields the array order overall.
      layers.forEach((spec) => {
        subLayerIds(spec).forEach((id) => {
          if (map.getLayer(id)) map.moveLayer(id);
        });
      });
      // three.js custom layers (the 3D fuel stand, mounted by
      // StoryFuel3DLayers rather than declared here) draw above the 2D stack.
      // (Custom layers are absent from mapbox's LayerSpecification union.)
      const styleLayers = (map.getStyle()?.layers ?? []) as { id: string; type: string }[];
      styleLayers.forEach((layer) => {
        if (layer.type === "custom" && map.getLayer(layer.id)) map.moveLayer(layer.id);
      });

      // Refresh the interactive-layer index for the delegated click handler.
      interactiveIdsRef.current.clear();
      layers.forEach((spec) => {
        if (spec.kind === "geojson-circle" && spec.interactive && spec.visible) {
          subLayerIds(spec).forEach((id) =>
            interactiveIdsRef.current.set(id, spec.id)
          );
        }
      });
    }, [layers, loaded]);

    // Late-arriving fit fallback (e.g. boundary loads after mount) when the
    // constructor couldn't establish a view and no camera was ever applied.
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !loaded || initialViewAppliedRef.current) return;
      const feature = boundaryToFeature(fitTo);
      if (feature && !camera && !appliedCameraRef.current) {
        const bounds = bboxOfFeature(feature);
        if (bounds) {
          initialViewAppliedRef.current = true;
          map.fitBounds(bounds, { padding: 60, duration: 0 });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitTo, loaded]);

    // Camera moves (fly/jump once per distinct camera).
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !camera) return;
      const key = JSON.stringify(camera);
      if (appliedCameraRef.current === key) return;
      appliedCameraRef.current = key;
      const options = {
        center: camera.center,
        zoom: camera.zoom,
        pitch: camera.pitch,
        bearing: camera.bearing,
      };
      if (transition === "jump") {
        map.jumpTo(options);
      } else {
        map.flyTo({ ...options, duration: 2500, essential: true });
      }
    }, [camera, transition]);

    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      setHandlers(map, interactive);
    }, [interactive, loaded]);

    return (
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", ...style }}
      />
    );
  }
);

StoryMapCanvas.displayName = "StoryMapCanvas";

export default StoryMapCanvas;
